import {
  createTheme,
  type Theme,
  type ThemeOptions,
} from "@mui/material/styles";

import { colors } from "Infrastructure/Themes/Colors";

export enum Spacing {
  XS = 1,
  S = 2,
  M = 4,
  L = 8,
  XL = 12,
  XXL = 16,
  XXXL = 24,
}

export enum Radius {
  S = "2px",
  M = "5px",
  L = "8px",
  XL = "12px",
  XXL = "16px",
  FULL = "9999px",
}

export type ColorsType = {
  PrimaryMain: string;
  PrimaryLight: string;
  PrimaryDark: string;
  PrimaryContrastText: string;
  SecondaryMain: string;
  SecondaryLight: string;
  SecondaryDark: string;
  SuccessMain: string;
  SuccessLight: string;
  SuccessDark: string;
  InfoMain: string;
  InfoLight: string;
  InfoDark: string;
  WarningMain: string;
  WarningLight: string;
  WarningDark: string;
  WarningPale: string;
  ErrorMain: string;
  ErrorLight: string;
  ErrorDark: string;
  TextPrimary: string;
  TextSecondary: string;
  TextDisabled: string;
  TextTertiary: string;
  TextLight: string;
  BackgroundDefault: string;
  BackgroundInput: string;
  BackgroundTile: string;
  BackgroundTileHover: string;
  BackgroundScrim: string;
  Border: string;
  BorderSubtle: string;
  BorderTable: string;
  ReactionAccent: string;
  White: string;
  Black: string;
  Gray: string;
};

const theme = (color: ColorsType): Theme => {
  const theme: ThemeOptions = {
    palette: {
      mode: "dark",
      primary: {
        main: color.PrimaryMain,
        light: color.PrimaryLight,
        dark: color.PrimaryDark,
        contrastText: color.PrimaryContrastText,
      },
      secondary: {
        main: color.SecondaryMain,
        light: color.SecondaryLight,
        dark: color.SecondaryDark,
        contrastText: color.PrimaryContrastText,
      },
      error: {
        main: color.ErrorMain,
        light: color.ErrorLight,
        dark: color.ErrorDark,
        contrastText: color.PrimaryContrastText,
      },
      success: {
        main: color.SuccessMain,
        light: color.SuccessLight,
        dark: color.SuccessDark,
        contrastText: color.PrimaryContrastText,
      },
      warning: {
        main: color.WarningMain,
        light: color.WarningLight,
        dark: color.WarningDark,
        contrastText: color.PrimaryContrastText,
      },
      info: {
        main: color.InfoMain,
        light: color.InfoLight,
        dark: color.InfoDark,
        contrastText: color.PrimaryContrastText,
      },
      text: {
        primary: color.TextPrimary,
        secondary: color.TextSecondary,
        disabled: color.TextDisabled,
      },
      background: {
        default: color.BackgroundDefault,
        paper: color.BackgroundDefault,
      },
      divider: "rgba(255, 255, 255, 0.12)",
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    spacing: 4,
    shape: {
      borderRadius: Radius.M,
    },
    typography: {
      fontFamily: "Open Sans",
      fontSize: 16,
      body1: {
        fontSize: 16,
        fontWeight: 400,
        letterSpacing: "0.2px",
      },
      body2: {
        fontWeight: 400,
        fontSize: 12,
        letterSpacing: "0.2px",
      },
      h1: {
        color: color.TextPrimary,
        fontWeight: 600,
        fontSize: 36,
        lineHeight: 1.6,
        letterSpacing: "0px",
      },
      h2: {
        color: color.TextPrimary,
        fontWeight: 600,
        fontSize: 24,
        lineHeight: 1.4,
      },
      h3: {
        color: color.TextPrimary,
        fontWeight: 600,
        fontSize: 20,
        lineHeight: 1.4,
      },
      h4: {
        color: color.TextPrimary,
        fontWeight: 700,
        fontSize: 24,
      },
      subtitle1: {
        fontWeight: 400,
        color: color.TextSecondary,
        fontSize: 18,
        lineHeight: 1.4,
      },
      subtitle2: {
        fontWeight: 400,
        color: color.TextPrimary,
        fontSize: 14,
        fontStyle: "italic",
        lineHeight: 1.4,
        letterSpacing: "0.2px",
      },
      button: {
        fontSize: 12,
        fontWeight: 600,
      },
      caption: {
        fontSize: 12,
        color: color.TextSecondary,
        fontWeight: 400,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "html, body, :root": {
            margin: 0,
            colorScheme: "dark",
          },
          body: {
            scrollbarGutter: "stable",

            "& *::-webkit-scrollbar-thumb": {
              backgroundColor: color.TextPrimary,
              borderRadius: "4px",
            },

            "& *::-webkit-scrollbar": {
              width: "4px",
              height: "4px",
              borderRadius: "4px",
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: "small",
          fullWidth: true,
        },
      },
      MuiOutlinedInput: {
        defaultProps: {
          size: "small",
          fullWidth: true,
          notched: false,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: 12,
            lineHeight: 1.4,
            height: 33,
            backgroundColor: theme.colors.backgroundInput,
            borderRadius: Radius.M,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.colors.border,
              borderRadius: Radius.M,
              transition: "border-color 200ms",
            },
            "&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.text.secondary,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.primary.main,
              borderWidth: "1px",
            },
            "&.Mui-disabled": {
              backgroundColor: `${theme.palette.text.disabled}80`,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.colors.border,
              },
            },
            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.error.main,
            },
            "&.MuiInputBase-multiline": {
              height: "auto",
              padding: theme.spacing(2, 3),
            },
          }),
          input: ({ theme }) => ({
            boxSizing: "border-box",
            "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus":
              {
                boxShadow: `0 0 0px 1000px ${theme.colors.backgroundInput} inset`,
                WebkitTextFillColor: theme.palette.common.white,
              },
            "&[type='number']::-webkit-inner-spin-button, &[type='number']::-webkit-outer-spin-button":
              { WebkitAppearance: "none", margin: 0 },
            "&[type='number']": { MozAppearance: "textfield" },
          }),
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            margin: 0,
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 1.4,
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          size: "small",
          fullWidth: true,
        },
        styleOverrides: {
          select: ({ theme }) => ({
            display: "flex",
            alignItems: "center",
            padding: theme.spacing(2, 2),
          }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: 12,
            fontWeight: 400,
            "& .MuiTouchRipple-root": {
              color: theme.palette.primary.main,
            },
          }),
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            padding: 2,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            padding: 2,
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: -2,
          },
          label: {
            fontSize: "12px",
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          clearIndicator: ({ theme }) => ({
            marginRight: theme.spacing(1),
            height: 33,
          }),
          paper: {
            marginTop: 0,
            marginBottom: 0,
          },
          option: ({ theme }) => ({
            fontSize: 12,
            fontWeight: 400,
            padding: theme.spacing(1, 2),
            "& .MuiTouchRipple-root": {
              color: theme.palette.primary.main,
            },
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            "& .MuiPickersBasePicker-container .MuiPickerDTTabs-tabs .MuiButtonBase-root":
              {
                color: color.White,
              },
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
            "& .MuiSvgIcon-root": {
              color: "inherit",
            },
            "& .MuiIconButton-root": {
              color: theme.palette.text.secondary,
            },
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            "&.Mui-disabled": {
              opacity: 0.4,
            },
          },
          text: {
            fontSize: 14,
          },
          containedPrimary: ({ theme }) => ({
            "&.Mui-disabled": {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          }),
          outlinedPrimary: ({ theme }) => ({
            "&.Mui-disabled": {
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
            },
          }),
        },
      },
    },
  };

  const customVariables = {
    colors: {
      border: color.Border,
      borderSubtle: color.BorderSubtle,
      borderTable: color.BorderTable,
      backgroundInput: color.BackgroundInput,
      backgroundTile: color.BackgroundTile,
      backgroundTileHover: color.BackgroundTileHover,
      backgroundScrim: color.BackgroundScrim,
      textTertiary: color.TextTertiary,
      textLight: color.TextLight,
      background: color.BackgroundDefault,
      reactionAccent: color.ReactionAccent,
    },
    shadow: {
      primary: "none",
      primaryHover: "0px 0px 15px 0px rgba(0, 0, 0, 0.15) inset",
    },
    borderRadius: "12px",
    vh: (val: number) => {
      return `calc(var(--vh, 1vh) * ${val})`;
    },
  };

  return createTheme(Object.assign(theme, customVariables)) as Theme;
};

export const getTheme = () => {
  return theme(colors());
};
